<template>
    <Card>
        <FetchTable
            :handle="tableHandle"
            :url="`/projects/${$route.params.projectUuid}/pim/products/${$route.params.productUuid}/publications`"
            :columns="table.columns"
            v-model:orders="table.orders"
        >
            <template #td-id="{row}">
                <router-link :to="{name: 'Product', params: {integrationUuid: row.channel.uuid, integrationProductUuid: row.uuid}}">
                    {{ row.id }}
                </router-link>
                <small class="text-muted d-block" v-if="row.second_source_id">{{ row.second_source_id }}</small>
            </template>
            <template #td-channel="{row}">
                <div class="d-flex align-items-center" style="max-width: 100%">
                    <img
                        :src="getIntegrationServiceLogo(row.channel.service)"
                        class="channel-logo mr-2 flex-shrink-0"
                        :alt="row.channel.name"
                        :title="row.channel.name"
                    />
                    <div class="d-flex flex-column" style="overflow: hidden">
                        <span class="text-truncate">{{ row.channel.name }}</span>
                        <small class="text-muted" v-if="row.channel.type">{{ $t(`labels.type_${row.channel.type}`) }}</small>
                    </div>
                </div>
            </template>
            <template #td-category="{row}">
                <AppNullableCell>
                    <template #default v-if="row.category">
                        <span class="long-string" :title="row.category.name">{{ row.category.name }}</span>
                    </template>
                </AppNullableCell>
            </template>
            <template #td-image="{row}">
                <ImageZoomComponent :url="row.image_url ?? PLACEHOLDER_IMAGE_URL" :alt="row.name"/>
            </template>
            <template #td-sku="{row}">
                <AppNullableCell>
                    <template #default v-if="row.sku">
                        <span class="long-string">{{ row.sku }}</span>
                    </template>
                </AppNullableCell>
            </template>
            <template #td-name="{row}">
                <AppNullableCell>
                    <template #default v-if="row.name">
                        <span class="long-string">{{ row.name }}</span>
                    </template>
                </AppNullableCell>
            </template>
            <template #td-price="{row}">
                <AppNullableCell>
                    <template #default v-if="getActualPrice(row) !== null">
                        <span :class="{'text-danger': row.promo_price !== null}">
                            {{ Price.factory(getActualPrice(row)).toCurrencyString('UAH') }}
                        </span>
                        <span class="text-muted d-block" v-if="hasDiscount(row)">
                            <s>{{ Price.factory(row.price).toCurrencyString('UAH') }}</s>
                        </span>
                    </template>
                </AppNullableCell>
            </template>
            <template #td-balance="{row}">
                {{ Amount.factory(row.balance ?? 0).toMeasurementString(product.measurement) }}
            </template>
            <template #td-export_enabled="{row}">
                <AppSwitch v-if="row.channel.type !== SUPPLIER_TYPE" :model-value="row.export_enabled" disabled />
                <span v-else></span>
            </template>
        </FetchTable>
    </Card>
</template>
<script>
import Card from '../../../../../components/new/CardComponent.vue';
import FetchTable from '../../../../../components/new/table/FetchTable.vue';
import { createTableHandle } from '../../../../../components/new/table/tableHandle';
import ImageZoomComponent from '../../../../../components/new/image/ImageZoomComponent.vue';
import AppNullableCell from '../../../../components/tables/AppNullableCell.vue';
import AppSwitch from '../../../../../components/new/form/switch/AppSwitch.vue';
import Amount from '../../../../../services/Amount.ts';
import Price from '../../../../../services/Price/Price.ts';

import { PLACEHOLDER_IMAGE_URL } from '../../../../../constants';
import { getIntegrationServiceLogo } from '../../../../enums/IntegrationServiceEnum';

const SUPPLIER_TYPE = 'supplier';

export default {
    name: "PublicationsPage",
    components: {
        Card, FetchTable, ImageZoomComponent, AppNullableCell, AppSwitch
    },
    props: {
        product: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            tableHandle: createTableHandle(),
            Amount,
            Price,
            PLACEHOLDER_IMAGE_URL,
            SUPPLIER_TYPE,
            getIntegrationServiceLogo,
            table: {
                orders: [{
                    column: 'id',
                    dir: 'desc'
                }],
                columns: [
                    {key: 'id', name: this.$t('table.titles.id'), width: '150px', type: 'string', order: true},
                    {key: 'channel', name: this.$t('table.titles.integration'), width: '200px', type: 'string', order: false},
                    {key: 'category', name: this.$t('table.titles.category'), width: '150px', type: 'string', order: false},
                    {key: 'image', name: this.$t('table.titles.image'), width: '100px', type: 'string', order: false},
                    {key: 'sku', name: this.$t('table.titles.sku'), width: '150px', type: 'string', order: false},
                    {key: 'name', name: this.$t('table.titles.name'), width: '300px', type: 'string', order: false},
                    {key: 'price', name: this.$t('table.titles.price'), width: '150px', type: 'number', order: true},
                    {key: 'balance', name: this.$t('table.titles.balance'), width: '150px', type: 'number', order: true},
                    {key: 'export_enabled', name: this.$t('table.titles.published_at'), width: '150px', type: 'string', order: true}
                ]
            }
        }
    },
    methods: {
        getActualPrice(row) {
            const price = row.promo_price ?? row.sale_price ?? row.price ?? null;

            return price;
        },
        /**
         * The regular price is only worth showing when something cheaper replaced it.
         */
        hasDiscount(row) {
            const hasDiscount = row.price !== null && (row.promo_price !== null || row.sale_price !== null);

            return hasDiscount;
        }
    },
    mounted() {
        this.tableHandle.fetchRows()
    }
}
</script>
<style scoped>
.channel-logo {
    border-radius: 5px;
    object-fit: cover;
    object-position: center;
    width: 20px;
    height: 20px;
}
.long-string {
    width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    line-clamp: 2;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
}
</style>
